import React from 'react';

import Explorer, { useExplorerMapDataRefreshKey } from '@components/Explorer';
import type { SearchTargets } from '@components/Explorer';
import { CreatePlanPromptModal } from '@components/Prompt';
import ScreenContainer from '@components/ScreenContainer';
import { useFollowing } from '@context/FollowingContext';
import { useGroups } from '@context/GroupsContext';
import { useCurrentUser } from '@context/UsersContext';
import { useNotificationListeners } from '@helpers/Notifications';
import { auth } from '@helpers/firebase/Initialize';
import { createAppNavigation } from '@navigation/AppNavigation';
import { useHeader } from '@navigation/Header';
import { HomeScreenProps } from '@navigation/Types';

const HOME_SEARCH_TARGETS: SearchTargets = { users: true, groups: true, places: true };

export default function HomeScreen({ navigation, route }: HomeScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  useHeader({ appNavigation, navigation });
  useNotificationListeners({ appNavigation });

  const { userDoc } = useCurrentUser();
  const { userGroupIds } = useGroups();
  const { followingUserIdsLoading, followingUserIdsError, followingUserIds } = useFollowing();
  const mapDataRefreshKey = useExplorerMapDataRefreshKey({ navigation, route });
  const currentUserId = auth.currentUser?.uid ?? userDoc?.userId;
  const explorerUserIds = React.useMemo(() => {
    const userIds = new Set<string>();
    if (currentUserId) userIds.add(currentUserId);
    followingUserIds.forEach((userId) => userIds.add(userId));
    return [...userIds.values()];
  }, [currentUserId, followingUserIds]);
  const explorerGroupIds = React.useMemo(() => [...userGroupIds.values()], [userGroupIds]);

  return (
    <ScreenContainer error={followingUserIdsError} testID="screens.HomeScreen.Background">
      <CreatePlanPromptModal onCreatePlanPress={appNavigation.openCreatePlan} />
      <Explorer
        mapDataRefreshKey={mapDataRefreshKey}
        mapLoading={followingUserIdsLoading}
        onChatPress={appNavigation.openChat}
        onCreatePlanPress={appNavigation.openCreatePlan}
        onGroupPress={appNavigation.openGroup}
        onPlanNavigationPress={appNavigation.openPlan}
        onUserNavigationPress={appNavigation.openProfile}
        queryGroupIds={explorerGroupIds}
        queryUserIds={explorerUserIds}
        searchTargets={HOME_SEARCH_TARGETS}
        mapTestID="screens.HomeScreen.Map"
        testID="screens.HomeScreen.FlatList.Plans"
      />
    </ScreenContainer>
  );
}
